import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

// Import all translations
import { de } from './translations/de'
import { en } from './translations/en'
import { fr } from './translations/fr'

export type LangCode = 'de' | 'en' | 'fr'

// Recursively convert all literal string types to string
type DeepString<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepString<T[K]>
}

export type Translations = DeepString<typeof de>

const translationMap: Record<LangCode, Translations> = {
  de, en, fr,
}

export const languages: { code: LangCode; name: string; flag: string }[] = [
  { code: 'de', name: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'en', name: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'fr', name: 'Fran\u00e7ais', flag: '\u{1F1EB}\u{1F1F7}' },
]

interface I18nContextType {
  lang: LangCode
  setLang: (lang: LangCode) => void
  t: Translations
}

const I18nContext = createContext<I18nContextType>({
  lang: 'de',
  setLang: () => {},
  t: de,
})

const STORAGE_KEY = 'parkbox_language'

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<LangCode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && saved in translationMap) return saved as LangCode
    return 'de'
  })

  const setLang = useCallback((newLang: LangCode) => {
    setLangState(newLang)
    localStorage.setItem(STORAGE_KEY, newLang)
    document.documentElement.lang = newLang
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const t = translationMap[lang] || de

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  return useContext(I18nContext)
}
